/**
 * Layout des pages du portail étudiant.
 * Look mobile-first avec dégradé bleu (couleurs 2i).
 */
import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { portailApi } from './services/portailApi';
import { NotificationsBell } from './components/NotificationsBell';
import { InstallPrompt } from './components/InstallPrompt';

interface Props {
  children: React.ReactNode;
  showHeader?: boolean;
  title?: string;
  fullWidth?: boolean;
  /** Cible du bouton retour ; par défaut : /portail/accueil. Mettre `null` pour cacher. */
  backTo?: string | null;
}

export const PortailLayout: React.FC<Props> = ({ children, showHeader = false, title, fullWidth = false, backTo }) => {
  const isAuthed = !!portailApi.getToken();
  const navigate = useNavigate();
  const location = useLocation();
  // Bouton back : actif sur toutes les pages sauf accueil et login.
  const onAccueil = location.pathname === '/portail/accueil' || location.pathname === '/portail';
  const showBack = isAuthed && backTo !== null && !onAccueil;
  const backTarget = backTo || '/portail/accueil';

  // Dark mode forcé sur le portail étudiant — restauré au démontage pour
  // ne pas polluer le back-office si l'utilisateur navigue entre les deux
  // dans le même onglet.
  useEffect(() => {
    const wasAlreadyDark = document.documentElement.classList.contains('dark');
    if (!wasAlreadyDark) document.documentElement.classList.add('dark');
    return () => {
      if (!wasAlreadyDark) document.documentElement.classList.remove('dark');
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100">
      {showHeader && (
        <header className="bg-gray-900 border-b border-gray-800 sticky top-0 z-30 backdrop-blur-sm">
          <div className={`${fullWidth ? 'max-w-3xl' : 'max-w-md'} mx-auto px-4 py-3 flex items-center gap-2`}>
            {showBack && (
              <button
                onClick={() => navigate(backTarget)}
                className="p-1.5 -ml-1.5 text-gray-300 hover:text-white hover:bg-gray-800 rounded-full"
                aria-label="Retour"
              >
                <ArrowLeft size={18} />
              </button>
            )}
            <img src="/logo.png" alt="2i" className="w-9 h-9 rounded-md flex-shrink-0 ring-1 ring-amber-500/30 bg-white p-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-gray-100 text-sm leading-none truncate">Institut International 2i</div>
              {title && <div className="text-xs text-gray-400 mt-1 truncate">{title}</div>}
            </div>
            {isAuthed && <NotificationsBell />}
          </div>
        </header>
      )}
      <main className={`${fullWidth ? 'max-w-3xl' : 'max-w-md'} mx-auto px-4 py-5`}>
        {children}
      </main>
      <InstallPrompt />
    </div>
  );
};
